import { useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, Building, MoreHorizontal, Pencil, Plus, Receipt } from 'lucide-react'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import {
  PageContainer, PageCard, PageHeader, PageHeaderContent,
  PageHeaderTitle, PageHeaderHeading, PageContent
} from '@/components/ui/page'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { OrgUsersTable } from '@/components/admin/organizations/OrgUsersTable'
import { OrgPhoneNumbersTable } from '@/components/admin/organizations/OrgPhoneNumbersTable'
import { KontingentEditSheet } from '@/components/admin/billing/KontingentEditSheet'
import { TopUpSheet } from '@/components/admin/billing/TopUpSheet'
import { useAdminOrganization } from '@/hooks/admin/useAdminOrganization'
import { useAdminBillingBalance } from '@/hooks/admin/useAdminBillingBalance'

export const Route = createFileRoute('/admin/organizations/$orgId')({
  component: OrganizationDetail,
  errorComponent: RouteErrorFallback,
})

function OrganizationDetail() {
  const { orgId } = Route.useParams()
  const { data: organization } = useAdminOrganization(orgId)
  const { data: billing } = useAdminBillingBalance(orgId)
  const [editAllowanceOpen, setEditAllowanceOpen] = useState(false)
  const [topUpOpen, setTopUpOpen] = useState(false)

  return (
    <>
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" asChild>
                  <Link to="/admin/organizations">
                    <ArrowLeft className="h-4 w-4" />
                  </Link>
                </Button>
                <Building className="h-5 w-5 text-muted-foreground" />
                <PageHeaderHeading>{organization?.name ?? orgId}</PageHeaderHeading>
              </div>
            </PageHeaderTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setEditAllowanceOpen(true)}>
                  <Pencil className="h-4 w-4" />
                  Kontingent bearbeiten
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setTopUpOpen(true)}>
                  <Plus className="h-4 w-4" />
                  Guthaben hinzufügen
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link
                    to="/admin/billing/$orgId"
                    params={{ orgId }}
                    className="flex items-center gap-2"
                  >
                    <Receipt className="h-4 w-4" />
                    Abrechnung ansehen
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </PageHeaderContent>
        </PageHeader>
        <PageContent className="p-4 gap-2">
          <OrgUsersTable organizationId={orgId} className="flex-1 min-h-0" />
          <OrgPhoneNumbersTable organizationId={orgId} />
        </PageContent>
      </PageCard>
    </PageContainer>

    {organization && (
      <KontingentEditSheet
        open={editAllowanceOpen}
        onOpenChange={setEditAllowanceOpen}
        organizationId={orgId}
        organizationName={organization.name}
        currentAllowance={billing?.allowance_minutes ?? 0}
      />
    )}

    {organization && (
      <TopUpSheet
        open={topUpOpen}
        onOpenChange={setTopUpOpen}
        organizationId={orgId}
        organizationName={organization.name}
      />
    )}
    </>
  )
}
